import { useCallback, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import {
  Flex,
  Text,
  Button,
  Spinner,
  Table,
  Section,
  IconButton,
} from "@radix-ui/themes";
import { ChevronRightIcon, TrashIcon } from "@radix-ui/react-icons";

import { AIsheet } from "@/v2/assets/icons/AIsheet";
import { useEnrichmentApi } from "@/v2/entities/enrichment/Enrichment.api";
import type {
  TicketSystemSyncMapping,
  Enrichment,
  TargetType,
} from "@/v2/entities/enrichment/Enrichment.model";
import { useIntegrations } from "@/api/integration/Integration.api";
import { isTicketSystemIntegration } from "@/api/integration/Integration.model";
import { integrationNameToGalleryItem } from "@/api/integration/integrationNameToGalleryItem";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import styles from "./SyncPage.module.scss";
import { useCustomFieldApi } from "@/v2/entities/custom-field/CustomField.api";

interface OutputKeyRow {
  outputKey: string;
  label: string;
}

function parseOutputKeys(enrichment: Enrichment | undefined): OutputKeyRow[] {
  if (!enrichment?.output) return [];
  try {
    const output =
      typeof enrichment.output === "string"
        ? JSON.parse(enrichment.output)
        : enrichment.output;
    if (!Array.isArray(output)) return [];
    return output
      .map((item: { name?: string; value?: string }) => {
        const name = item.name ?? item.value ?? "";
        const value =
          item.value ?? (name ? name.toLowerCase().replace(/\s+/g, "_") : "");
        return { outputKey: value, label: name };
      })
      .filter((row: OutputKeyRow) => row.outputKey || row.label);
  } catch {
    return [];
  }
}

interface MappingValue {
  targetType: TargetType;
  targetValue: string;
}

function getMappingByKey(
  mappings: TicketSystemSyncMapping[],
  outputKey: string
): MappingValue | null {
  const mapping = mappings.find((m) => m.outputKey === outputKey);
  if (!mapping) return null;
  return { targetType: mapping.targetType, targetValue: mapping.targetValue };
}

export default function SyncPage() {
  const { enrichmentId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const enrichmentApi = useEnrichmentApi();
  const customFieldApi = useCustomFieldApi();

  const [localMappings, setLocalMappings] = useState<
    Record<string, MappingValue | null>
  >({});
  const [tagPrefixInputs, setTagPrefixInputs] = useState<
    Record<string, string>
  >({});

  const { data: enrichment, isLoading } = useQuery({
    queryKey: ["enrichment", enrichmentId],
    queryFn: () => enrichmentApi.getById(enrichmentId || ""),
    enabled: !!enrichmentId,
  });

  const { data: ticketFields, isLoading: isLoadingTicketFields } = useQuery({
    queryKey: ["custom-field-keys"],
    queryFn: () => customFieldApi.getCustomFieldKeys(),
  });

  const { data: integrations } = useIntegrations();

  const ticketSystemInfo = useMemo(() => {
    const ticketSystemIntegration = integrations?.find(
      isTicketSystemIntegration
    );
    if (!ticketSystemIntegration) {
      return { name: "Ticket System", icon: null };
    }
    const galleryItem =
      integrationNameToGalleryItem[ticketSystemIntegration.name];
    return {
      name: galleryItem?.title ?? ticketSystemIntegration.name,
      icon: galleryItem?.svg ?? null,
    };
  }, [integrations]);

  const customFieldOptions = useMemo(() => {
    if (!ticketFields) return [];
    return ticketFields
      .filter((f) => f.isActive)
      .map((f) => ({
        value: f.externalId,
        label: f.key,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [ticketFields]);

  const getFieldLabel = useCallback(
    (mapping: MappingValue) => {
      if (mapping.targetType === "TAG") {
        return mapping.targetValue
          ? `Tag: ${mapping.targetValue}`
          : "Tag (no prefix)";
      }
      const field = customFieldOptions.find(
        (f) => f.value === mapping.targetValue
      );
      return field?.label ?? mapping.targetValue;
    },
    [customFieldOptions]
  );

  const updateMutation = useMutation({
    mutationFn: enrichmentApi.update,
    onSuccess: () => {
      toast("Sync saved successfully");
      queryClient.invalidateQueries(["enrichment", enrichmentId]);
    },
    onError: () => {
      toast.error("Failed to save sync");
    },
  });

  const outputRows = useMemo(() => parseOutputKeys(enrichment), [enrichment]);

  const mappingsFromEnrichment = useMemo(
    () => enrichment?.ticketSystemSyncMappings?.mappings ?? [],
    [enrichment]
  );

  const getMappingForOutputKey = useCallback(
    (outputKey: string): MappingValue | null => {
      if (outputKey in localMappings) {
        return localMappings[outputKey];
      }
      return getMappingByKey(mappingsFromEnrichment, outputKey);
    },
    [localMappings, mappingsFromEnrichment]
  );

  const handleSetMapping = useCallback(
    (outputKey: string, targetType: TargetType, targetValue: string) => {
      setLocalMappings((prev) => ({
        ...prev,
        [outputKey]: { targetType, targetValue },
      }));
    },
    []
  );

  const handleClearMapping = useCallback((outputKey: string) => {
    setLocalMappings((prev) => ({
      ...prev,
      [outputKey]: null,
    }));
  }, []);

  const handleTagPrefixChange = useCallback(
    (outputKey: string, value: string) => {
      setTagPrefixInputs((prev) => ({
        ...prev,
        [outputKey]: value,
      }));
    },
    []
  );

  const handleTagPrefixSubmit = useCallback(
    (outputKey: string) => {
      const prefix = tagPrefixInputs[outputKey] ?? "";
      handleSetMapping(outputKey, "TAG", prefix);
      setTagPrefixInputs((prev) => ({
        ...prev,
        [outputKey]: "",
      }));
    },
    [tagPrefixInputs, handleSetMapping]
  );

  const effectiveMappings = useMemo(
    (): TicketSystemSyncMapping[] =>
      outputRows
        .map((row) => {
          const mapping = getMappingForOutputKey(row.outputKey);
          if (!mapping) return null;
          return {
            outputKey: row.outputKey,
            targetType: mapping.targetType,
            targetValue: mapping.targetValue,
          };
        })
        .filter((m): m is TicketSystemSyncMapping => m !== null),
    [outputRows, getMappingForOutputKey]
  );

  const hasSyncConfigured = useMemo(
    () => mappingsFromEnrichment.length > 0,
    [mappingsFromEnrichment]
  );

  const handleSave = useCallback(() => {
    if (!enrichmentId || !enrichment) return;

    const filterRules =
      typeof enrichment.filterRules === "string"
        ? enrichment.filterRules
        : JSON.stringify(enrichment.filterRules ?? {});

    updateMutation.mutate({
      id: enrichmentId,
      name: enrichment.name,
      task: enrichment.task,
      filterRules,
      options: enrichment.options,
      isEnabled: enrichment.isEnabled,
      trigger: enrichment.trigger,
      sources: enrichment.sources ?? [],
      output: enrichment.output,
      ticketSystemSyncEnabled: true,
      ticketSystemSyncMappings: { mappings: effectiveMappings },
    });
  }, [enrichmentId, enrichment, effectiveMappings, updateMutation]);

  const handlePauseSync = useCallback(() => {
    if (!enrichmentId || !enrichment) return;

    const filterRules =
      typeof enrichment.filterRules === "string"
        ? enrichment.filterRules
        : JSON.stringify(enrichment.filterRules ?? {});

    updateMutation.mutate({
      id: enrichmentId,
      name: enrichment.name,
      task: enrichment.task,
      filterRules,
      options: enrichment.options,
      isEnabled: enrichment.isEnabled,
      trigger: enrichment.trigger,
      sources: enrichment.sources ?? [],
      output: enrichment.output,
      ticketSystemSyncEnabled: false,
      ticketSystemSyncMappings: enrichment.ticketSystemSyncMappings,
    });
  }, [enrichmentId, enrichment, updateMutation]);

  const handleStartSync = useCallback(() => {
    if (!enrichmentId || !enrichment) return;

    const filterRules =
      typeof enrichment.filterRules === "string"
        ? enrichment.filterRules
        : JSON.stringify(enrichment.filterRules ?? {});

    updateMutation.mutate({
      id: enrichmentId,
      name: enrichment.name,
      task: enrichment.task,
      filterRules,
      options: enrichment.options,
      isEnabled: enrichment.isEnabled,
      trigger: enrichment.trigger,
      sources: enrichment.sources ?? [],
      output: enrichment.output,
      ticketSystemSyncEnabled: true,
      ticketSystemSyncMappings: enrichment.ticketSystemSyncMappings,
    });
  }, [enrichmentId, enrichment, updateMutation]);

  const isSyncEnabled = enrichment?.ticketSystemSyncEnabled ?? false;

  if (isLoading) {
    return (
      <Flex align="center" justify="center" className={styles.parentContainer}>
        <Spinner />
      </Flex>
    );
  }

  if (!enrichment) {
    return (
      <Flex align="center" justify="center" className={styles.parentContainer}>
        <Text>Enrichment not found</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" className={styles.syncPageContainer}>
      <div className={styles.syncPageHeader}>
        <IconButton
          variant="ghost"
          color="gray"
          size="2"
          onClick={() => navigate("/enrichments")}
          aria-label="Back to enrichments"
          className={styles.syncPageHeader__iconBtn}
        >
          <AIsheet size={16} fill="plum" />
          Enrichments
        </IconButton>
        <span className={styles.syncPageHeader__chevron}>
          <ChevronRightIcon />
        </span>
        <div className={styles.syncPageHeader__controls}>
          <Flex align="center" gap="2">
            <span className={styles.syncPageHeader__name}>Sync</span>
          </Flex>
        </div>
      </div>
      <Flex direction="column" gap="7" className={styles.syncPageContent}>
        <Flex direction="row" align="center" justify="between">
          <Flex direction="row" align="center" gap="3">
            <Flex direction="column" gap="2">
              <Flex align="center" gap="2">
                <Flex
                  align="center"
                  justify="center"
                  className={styles.aiSheetIconWrapper}
                >
                  <AIsheet size={24} fill="plum" />
                </Flex>
                {ticketSystemInfo.icon && (
                  <div className={styles.ticketSystemIconWrapper}>
                    {ticketSystemInfo.icon}
                  </div>
                )}
              </Flex>
              <Text size="6" weight="bold">
                Sync {enrichment.name} with {ticketSystemInfo.name}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        <Flex direction="column" gap="2">
          <Text size="3" weight="medium">
            Map to {ticketSystemInfo.name}
          </Text>
          <Text size="2" color="gray" className={styles.descriptionText}>
            Select a {ticketSystemInfo.name} Ticket Field to store the
            enrichment data
          </Text>
        </Flex>

        <Section size="1" pt="0">
          <Flex direction="column" gap="3" width="100%">
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell className={styles.tableHeader}>
                    Value
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={styles.tableHeader}
                    align="right"
                  >
                    Field Mapping
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {outputRows.map((row) => {
                  const currentMapping = getMappingForOutputKey(row.outputKey);
                  return (
                    <Table.Row key={row.outputKey} className={styles.tableRow}>
                      <Table.Cell>
                        <Flex
                          align="center"
                          gap="2"
                          className={styles.valueTagWrapper}
                        >
                          <Text size="2">
                            {row.label || row.outputKey || "—"}
                          </Text>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell align="right">
                        {isLoadingTicketFields ? (
                          <Spinner size="1" />
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="solid"
                                color="gray"
                                highContrast
                                size="2"
                              >
                                {currentMapping
                                  ? getFieldLabel(currentMapping)
                                  : "+ Add Mapping"}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {currentMapping && (
                                <Flex
                                  justify="between"
                                  align="center"
                                  className={styles.dropdownHeader}
                                >
                                  <Text size="2" color="gray">
                                    {getFieldLabel(currentMapping)}
                                  </Text>
                                  <IconButton
                                    size="1"
                                    variant="ghost"
                                    highContrast
                                    onClick={() =>
                                      handleClearMapping(row.outputKey)
                                    }
                                  >
                                    <TrashIcon width={20} height={20} />
                                  </IconButton>
                                </Flex>
                              )}
                              <Flex
                                className={styles.customTagInputWrapper}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                <Input
                                  placeholder="Tag prefix (optional)"
                                  value={tagPrefixInputs[row.outputKey] || ""}
                                  onChange={(e) =>
                                    handleTagPrefixChange(
                                      row.outputKey,
                                      e.target.value
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleTagPrefixSubmit(row.outputKey);
                                    }
                                  }}
                                />
                                <Button
                                  color="gray"
                                  highContrast
                                  onClick={() =>
                                    handleTagPrefixSubmit(row.outputKey)
                                  }
                                  className={styles.addButton}
                                >
                                  Add
                                </Button>
                              </Flex>
                              <Text
                                size="2"
                                weight="medium"
                                className={styles.dropdownSectionLabel}
                              >
                                Custom Field
                              </Text>
                              <div className={styles.dropdownScrollArea}>
                                {customFieldOptions.map((field) => (
                                  <DropdownMenuItem
                                    key={field.value}
                                    onClick={() =>
                                      handleSetMapping(
                                        row.outputKey,
                                        "CUSTOM_FIELD",
                                        field.value
                                      )
                                    }
                                  >
                                    {field.label}
                                  </DropdownMenuItem>
                                ))}
                                {customFieldOptions.length === 0 && (
                                  <DropdownMenuItem disabled>
                                    No custom fields available
                                  </DropdownMenuItem>
                                )}
                              </div>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
                {outputRows.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={2}>
                      <Text align="center" size="2" color="gray">
                        No output values defined for this enrichment.
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Root>
          </Flex>
        </Section>
      </Flex>
      <div className={styles.syncPageFooter}>
        <Flex justify="end" gap="2">
          {hasSyncConfigured ? (
            <>
              {isSyncEnabled ? (
                <Button
                  color="gray"
                  variant="outline"
                  onClick={handlePauseSync}
                  disabled={updateMutation.isLoading}
                >
                  Pause Sync
                </Button>
              ) : (
                <Button
                  color="gray"
                  variant="outline"
                  onClick={handleStartSync}
                  disabled={updateMutation.isLoading}
                >
                  Start Sync
                </Button>
              )}
              <Button
                color="gray"
                highContrast
                onClick={handleSave}
                disabled={updateMutation.isLoading}
              >
                Update Sync
              </Button>
            </>
          ) : (
            <Button
              color="gray"
              highContrast
              onClick={handleSave}
              disabled={updateMutation.isLoading}
            >
              Create Sync
            </Button>
          )}
        </Flex>
      </div>
    </Flex>
  );
}
